import React from 'react';
import { EventHero } from "../components/EventHero";
import { UpcomingEvents } from "../components/UpcomingEvents";
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Zap, CheckCircle2, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { heroContent, services } from '../mock';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';
import { SEO_CONTENT } from '../data/seoContent';

export const Home = () => {
  React.useEffect(() => {
    try { if (window.sessionStorage.getItem('lumi_greeted')) return; } catch (e) {}
    const t = setTimeout(() => {
      try { window.sessionStorage.setItem('lumi_greeted', '1'); } catch (e) {}
      const b = document.getElementById('lumi-launcher');
      if (b) b.click();
    }, 6000);
    return () => clearTimeout(t);
  }, []);

  const { t } = useTranslation();

  const statsData = [
    { label: t('home.yearsExcellence'), value: "15+" },
    { label: t('home.koreanPartners'), value: "5+" },
    { label: t('home.industriesServed'), value: "4" },
    { label: t('home.projectsDelivered'), value: "50+" }
  ];

  return (
    <div className="min-h-screen">
      <EventHero />
      <SEO {...SEO_CONTENT.home} />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden py-28 md:py-0">
        <div className="absolute inset-0">
          <img
            src={heroContent.image}
            alt="LAMKO Innovation"
            className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/85 to-gray-900/75"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 md:mb-6 tracking-tight">
            {t('home.heroTitle')}
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-teal-400 mb-3 md:mb-4 font-medium">
            {t('home.heroSubtitle')}
          </p>
        <p className="hidden sm:block text-sm sm:text-base md:text-lg text-orange-300 mb-4 font-medium italic max-w-2xl mx-auto">
            {t('home.heroTagline')}
          </p>
          <p className="text-sm sm:text-base md:text-lg text-gray-300 mb-6 md:mb-8 max-w-3xl mx-auto">
            {t('home.heroDescription')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center px-4">
            <Link to="/partner" data-testid="hero-partner-cta">
              <Button size="lg" className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 text-white px-6 md:px-8 py-4 md:py-6 text-base md:text-lg shadow-lg shadow-orange-900/40">
                {t('nav.partnerWithUs')}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/services">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-gray-900 px-6 md:px-8 py-4 md:py-6 text-base md:text-lg bg-transparent">
                {t('home.heroCta')}
              </Button>
            </Link>
            <Link to="/get-in-touch">
              <Button size="lg" variant="ghost" className="w-full sm:w-auto text-white hover:bg-white/10 px-6 md:px-8 py-4 md:py-6 text-base md:text-lg">
                {t('home.contactUs')}
              </Button>
            </Link>
          </div>
        </div>

        <div className="hidden md:block absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {statsData.map((stat, index) =>
            <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-orange-600 mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            )}
          </div>
        </div>
      </section>
      <section style={{ background: "radial-gradient(circle at 15% 20%, rgba(239,68,68,0.16), transparent 40%), radial-gradient(circle at 50% 8%, rgba(16,185,129,0.14), transparent 40%), radial-gradient(circle at 88% 25%, rgba(168,85,247,0.16), transparent 40%), #070B16" }} className="text-white">
        <div style={{ height: "3px", background: "linear-gradient(90deg,#F87171,#FB923C,#FBBF24,#34D399,#60A5FA,#A78BFA)" }} />
        <div className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs tracking-widest uppercase" style={{ color: "#9FE1CB" }}>Meet LUMI, LAMKO's AI assistant</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold leading-tight">Scope your project with <span style={{ background: "linear-gradient(90deg,#F87171,#FB923C,#34D399,#60A5FA,#A78BFA)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>LUMI.</span></h2>
            <p className="mt-3 text-base" style={{ color: "#AEBBD0" }}>Ask about custom synthesis, scale-up, electronics-grade purification, and production-scale supply of semiconductor, display (OLED) and specialty materials. NDA-first, any time.</p>
            <Link to="/lumi" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white font-semibold" style={{ background: "linear-gradient(90deg,#EA580C,#F59E0B)" }}>Open LUMI workspace <ArrowRight size={18} /></Link>
          </div>
          <Link to="/lumi" className="block rounded-xl px-4 py-4" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(125,150,190,0.35)", boxShadow: "0 0 30px rgba(96,165,250,0.12)" }}>
            <div className="flex items-center gap-2">
              <span className="text-sm" style={{ color: "#8595AE" }}>What do you want to do?</span>
              <span className="ml-auto text-white text-sm font-medium px-3 py-1.5 rounded-lg" style={{ background: "linear-gradient(90deg,#EA580C,#F59E0B)" }}>Start a project</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="text-xs px-3 py-1.5 rounded-full" style={{ background: "rgba(96,165,250,0.10)", border: "1px solid rgba(96,165,250,0.3)", color: "#BCD6F5" }}>Custom synthesis</span>
              <span className="text-xs px-3 py-1.5 rounded-full" style={{ background: "rgba(96,165,250,0.10)", border: "1px solid rgba(96,165,250,0.3)", color: "#BCD6F5" }}>OLED materials</span>
              <span className="text-xs px-3 py-1.5 rounded-full" style={{ background: "rgba(96,165,250,0.10)", border: "1px solid rgba(96,165,250,0.3)", color: "#BCD6F5" }}>Purify to spec</span>
            </div>
          </Link>
        </div>
      </section>

      <section className="bg-slate-900 text-white py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-teal-400 text-sm font-semibold tracking-wider mb-2">Why LAMKO</div>
          <h2 className="text-2xl md:text-4xl font-bold mb-3">Built to be qualified</h2>
          <p className="text-slate-300 text-base md:text-lg max-w-3xl mb-8">We run the high-value chemistry in-house and govern every step, ours and our partners', under a documented quality system, so you get the same result, lot after lot.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-8">
            <div className="bg-white/5 border border-white/10 rounded-xl p-5"><div className="text-teal-400 text-2xl font-bold">200+</div><div className="text-slate-300 text-sm mt-1">SOPs governing every step</div></div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-5"><div className="text-teal-400 text-2xl font-bold">7</div><div className="text-slate-300 text-sm mt-1">Functional domains</div></div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-5"><div className="text-teal-400 text-2xl font-bold">ICH + ISO</div><div className="text-slate-300 text-sm mt-1">Principles by design</div></div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-5"><div className="text-teal-400 text-2xl font-bold">Every lot</div><div className="text-slate-300 text-sm mt-1">Ships with a CoA</div></div>
          </div>
          <a href="/quality" className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg px-7 py-3 transition-colors">See our quality system &rarr;</a>
        </div>
      </section>


      {/* Core Value Proposition */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('home.coreValueTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            <Card className="border-t-4 border-t-teal-600 hover:shadow-xl transition-shadow duration-300">
              <CardContent className="pt-6 md:pt-8">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                  <Zap className="text-teal-600" size={20} />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3">
                  {t('home.strategicPlatform')}
                </h3>
                <p className="text-sm md:text-base text-gray-600">
                  {t('home.strategicPlatformDesc')}
                </p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-t-orange-600 hover:shadow-xl transition-shadow duration-300">
              <CardContent className="pt-6 md:pt-8">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                  <Target className="text-orange-600" size={20} />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3">
                  {t('home.crdmoModel')}
                </h3>
                <p className="text-sm md:text-base text-gray-600">
                  {t('home.crdmoModelDesc')}
                </p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-t-teal-600 hover:shadow-xl transition-shadow duration-300">
              <CardContent className="pt-6 md:pt-8">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                  <MapPin className="text-teal-600" size={20} />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3">
                  {t('home.seoulHQ')}
                </h3>
                <p className="text-sm md:text-base text-gray-600">
                  {t('home.seoulHQDesc')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('home.ourCapabilities')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('home.capabilitiesSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service) =>
            <Card key={service.id} className="group hover:shadow-2xl transition-all duration-300 overflow-hidden">
                <div className="relative h-48 overflow-hidden">
                  <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent"></div>
                </div>
                <CardContent className="pt-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-2 mb-4">
                    {service.benefits.slice(0, 3).map((benefit, idx) =>
                  <li key={idx} className="flex items-start space-x-2 text-sm text-gray-600">
                        <CheckCircle2 size={16} className="text-teal-600 mt-0.5 flex-shrink-0" />
                        <span>{benefit}</span>
                      </li>
                  )}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white">
                {t('home.exploreServices')}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Mid-page Partner CTA Strip */}
      <section className="relative py-10 md:py-14 bg-gradient-to-r from-orange-600 via-orange-500 to-teal-600 overflow-hidden" data-testid="mid-partner-cta">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(255,255,255,0.15),_transparent_60%)]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-white text-center md:text-left">
            <h3 className="text-2xl md:text-3xl font-bold leading-tight mb-1">Have a project in mind?</h3>
            <p className="text-base md:text-lg text-white/90">Let's build the solution together - from feasibility to commercial supply.</p>
          </div>
          <Link to="/partner">
            <Button size="lg" className="bg-white text-orange-700 hover:bg-gray-100 px-6 md:px-8 py-4 md:py-6 text-base md:text-lg font-semibold shadow-xl whitespace-nowrap">
              {t('nav.partnerWithUs')}
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>

      {/* Core Industry Experience Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('home.industryExperienceTitle')}
            </h2>
            <p className="text-2xl font-semibold text-orange-600 mb-6">
              {t('home.coreIndustryExp')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-8 items-center">
            <div className="flex justify-center">
              <a href="https://www.ictmumbai.edu.in/" target="_blank" rel="noopener noreferrer">
                <img src="/img/ext/5f294dd9cd.png" alt="ICT Mumbai" className="h-24 w-auto object-contain hover:scale-110 transition-transform duration-300" />
              </a>
            </div>
            <div className="flex justify-center">
              <a href="https://www.iitb.ac.in/" target="_blank" rel="noopener noreferrer">
                <img src="/img/ext/7f61ec48c9.png" alt="IIT Bombay" className="h-24 w-auto object-contain hover:scale-110 transition-transform duration-300" />
              </a>
            </div>
            <div className="flex justify-center">
              <a href="https://eng.skku.edu/eng/" target="_blank" rel="noopener noreferrer">
                <img src="/img/ext/970ea29545.png" alt="SKKU" className="h-24 w-auto object-contain hover:scale-110 transition-transform duration-300" />
              </a>
            </div>
            <div className="flex justify-center">
              <a href="https://www.khu.ac.kr/eng/user/main/view.do" target="_blank" rel="noopener noreferrer">
                <img src="/img/ext/cfa0ead9d5.png" alt="Kyung Hee University" className="h-24 w-auto object-contain hover:scale-110 transition-transform duration-300" />
              </a>
            </div>
            <div className="flex justify-center">
              <a href="https://www.kaist.ac.kr/kr/" target="_blank" rel="noopener noreferrer">
                <img src="/img/ext/7133b0f02d.png" alt="KAIST" className="h-24 w-auto object-contain hover:scale-110 transition-transform duration-300" />
              </a>
            </div>
            <div className="flex justify-center">
              <a href="https://www.wonik.com/" target="_blank" rel="noopener noreferrer">
                <img src="/img/ext/e7d10c0223.png" alt="Wonik" className="h-24 w-auto object-contain hover:scale-110 transition-transform duration-300" />
              </a>
            </div>
            <div className="flex justify-center">
              <a href="https://www.clariant.com" target="_blank" rel="noopener noreferrer">
                <img src="/img/ext/8dfd273bcf.png" alt="Clariant" className="h-24 w-auto object-contain hover:scale-110 transition-transform duration-300" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white" data-testid="bottom-partner-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-orange-600/15 border border-orange-600/30 text-orange-300 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-[0.18em] mb-5">
            <span className="w-1.5 h-1.5 bg-orange-400 rounded-full animate-pulse" />
            Ready to start?
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            From concept to commercial scale - let's get started.
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            {t('home.readyToAccelerateDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/partner">
              <Button size="lg" className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 text-lg shadow-lg shadow-orange-900/50">
                {t('nav.partnerWithUs')}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/get-in-touch">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg bg-transparent">
                {t('home.startConversation')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <UpcomingEvents />

      <section className="py-16 bg-slate-900">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="mb-3"><span className="inline-block bg-orange-600/20 text-orange-400 border border-orange-600/40 rounded-full px-4 py-1 text-sm font-semibold">{t('home.careersHiring')}</span></div>
          <h2 className="text-3xl font-bold text-white mb-3">{t('home.careersTitle')}</h2>
          <p className="text-gray-300 mb-6 max-w-3xl mx-auto">{t('home.careersText')}</p>
          <a href="/careers" className="inline-block bg-orange-600 hover:bg-orange-700 text-white font-semibold px-8 py-3 rounded-lg">{t('home.careersCta')}</a>
        </div>
      </section>
    </div>);
};
